import type { DateRange } from "@a2z/ui-v2";

/**
 * Labels for `@a2z/ui-v2`'s `DatePicker` / `DateRangePicker`.
 *
 * Month and weekday names come from `Intl`, not from the translation files:
 * they are the same strings in every product that has ever rendered a
 * calendar, and a hand-maintained list of twelve months in two locales is
 * twenty-four chances to ship a typo nobody reads closely.
 */

/** Egypt's week starts on Saturday. Not a default — a fact about the reader. */
export const EGYPT_FIRST_DAY_OF_WEEK = 6;

/**
 * Seven short weekday names, ROTATED so index 0 is `firstDayOfWeek`.
 *
 * The component draws this array straight across the header and offsets the
 * 1st of the month against the same index. Handing it an unrotated
 * Sunday-first list under a Saturday-first grid draws every date one column
 * out — and looks entirely plausible while doing it.
 */
export function weekdayNames(
  locale: string,
  firstDayOfWeek = EGYPT_FIRST_DAY_OF_WEEK,
): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: "short" });
  // 2024-01-07 is a Sunday, so `+ i` walks the week from index 0 = Sunday.
  return Array.from({ length: 7 }, (_, i) =>
    format.format(new Date(Date.UTC(2024, 0, 7 + ((firstDayOfWeek + i) % 7)))),
  );
}

export function monthLabel(locale: string, month: Date): string {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(month);
}

export function dayLabel(locale: string, date: Date): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(date);
}

/** `YYYY-MM-DD` (what the filter query carries) -> a local `Date`. */
export function dateFromIso(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  // Constructed locally, not via `new Date(iso)`: that parses as UTC, so east
  // of Greenwich the picker highlights the day before the one in the URL.
  return new Date(year, month - 1, day);
}

/** A `Date` -> `YYYY-MM-DD`, in the LOCAL day, for the same reason. */
export function isoFromDate(date: Date | undefined): string {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The trigger's summary of the current range. */
export function rangeSummary(
  locale: string,
  range: DateRange,
  allTimeLabel: string,
): string {
  if (!range.start && !range.end) return allTimeLabel;
  const short = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
  const start = range.start ? short.format(range.start) : "…";
  const end = range.end ? short.format(range.end) : "…";
  // One isolate around the WHOLE reading, separator included: two adjacent
  // `<bdi>`s would leave an Arabic paragraph to order the pair, and the reader
  // would see the end date first. This is a plain string with no element to
  // carry `dir`, so the isolate is the U+2066 … U+2069 pair.
  return `⁦${start} – ${end}⁩`;
}
